import React, { useState, useEffect, useRef } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import styles from './DateNavigator.module.css';

/* ═══════════════════════════════════════════════
   DATE HELPERS
   ═══════════════════════════════════════════════ */
const parseDateSafe = (date: any): Date => {
    if (!date) return new Date();
    if (date instanceof Date) return date;
    if (typeof date === 'string') {
        // If it's YYYY-MM-DD, add T12:00:00
        if (date.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
            return new Date(date + 'T12:00:00');
        }
        // If it already has a T, just parse it
        return new Date(date);
    }
    return new Date(date);
};

export const formatLocalDate = (date: any): string => {
    const d = parseDateSafe(date);
    if (isNaN(d.getTime())) return '';
    
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const getWeekRange = (date: any): { start: Date; end: Date } => {
    const d = parseDateSafe(date);
    if (isNaN(d.getTime())) return { start: new Date(), end: new Date() };

    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday
    const start = new Date(d.getFullYear(), d.getMonth(), diff, 0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 4); // Match backend: Monday to Friday
    end.setHours(23, 59, 59, 999);
    return { start, end };
};

export const getMonthRange = (date: any): { start: Date; end: Date } => {
    const d = parseDateSafe(date);
    if (isNaN(d.getTime())) return { start: new Date(), end: new Date() };

    const start = new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
    return { start, end };
};

export const getYearRange = (date: any): { start: Date; end: Date } => {
    const d = parseDateSafe(date);
    if (isNaN(d.getTime())) return { start: new Date(), end: new Date() };

    const start = new Date(d.getFullYear(), 0, 1, 0, 0, 0, 0);
    const end = new Date(d.getFullYear(), 11, 31, 23, 59, 59, 999);
    return { start, end };
};

export const formatRange = (start: Date, end: Date): string => {
    if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) return "";
    const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: '2-digit' };
    return `${start.toLocaleDateString('pt-BR', options)} - ${end.toLocaleDateString('pt-BR', options)}`;
};

const MONTHS = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

/* ═══════════════════════════════════════════════
   SUB-COMPONENTS
   ═══════════════════════════════════════════════ */
interface MiniCalendarProps {
    activeDate: Date;
    onSelect: (date: Date) => void;
}

const MiniCalendar: React.FC<MiniCalendarProps> = ({ activeDate, onSelect }) => {
    const [viewDate, setViewDate] = useState(new Date(activeDate));
    const [hoverDate, setHoverDate] = useState<Date | null>(null);

    useEffect(() => {
        if (activeDate) {
            setViewDate(new Date(activeDate));
        }
    }, [activeDate]);

    const firstDayOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    const startDay = firstDayOfMonth.getDay();
    const prevMonthLastDay = new Date(viewDate.getFullYear(), viewDate.getMonth(), 0).getDate();
    const daysInMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate();

    const days: { day: number; month: number; date: Date }[] = [];
    for (let i = startDay - 1; i >= 0; i--) {
        days.push({ day: prevMonthLastDay - i, month: -1, date: new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, prevMonthLastDay - i) });
    }
    for (let i = 1; i <= daysInMonth; i++) {
        days.push({ day: i, month: 0, date: new Date(viewDate.getFullYear(), viewDate.getMonth(), i) });
    }
    while (days.length % 7 !== 0) {
        const d = days.length - daysInMonth - startDay + 1;
        days.push({ day: d, month: 1, date: new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, d) });
    }

    const { start: activeStart, end: activeEnd } = getWeekRange(activeDate);
    const { start: hoverStart, end: hoverEnd } = hoverDate ? getWeekRange(hoverDate) : { start: null, end: null };

    const isSelected = (date: Date) => date >= activeStart && date <= activeEnd;
    const isHovered = (date: Date) => hoverDate && hoverStart && hoverEnd && date >= hoverStart && date <= hoverEnd;

    return (
        <div className={styles.calendarContainer}>
            <div className={styles.calendarHeader}>
                <h4 className={styles.calendarTitle}>{MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}</h4>
                <div className={styles.navButtons}>
                    <button 
                        onClick={() => {
                            const newDate = new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1);
                            setViewDate(newDate);
                        }} 
                        className={styles.navBtn}
                    >
                        <ChevronLeftIcon style={{ fontSize: 16 }} />
                    </button>
                    <button 
                        onClick={() => {
                            const newDate = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);
                            setViewDate(newDate);
                        }} 
                        className={styles.navBtn}
                    >
                        <ChevronRightIcon style={{ fontSize: 16 }} />
                    </button>
                </div>
            </div>
            <div className={styles.weekDaysHeader}>
                {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((d, i) => <div key={i}>{d}</div>)}
            </div>
            <div className={styles.daysGrid}>
                {days.map((d, i) => {
                    const selected = isSelected(d.date);
                    const hovered = isHovered(d.date);
                    return (
                        <div
                            key={i}
                            className={`
                                ${styles.dayCell}
                                ${d.month !== 0 ? styles.dayOtherMonth : styles.dayCurrentMonth}
                                ${selected ? styles.daySelected : hovered ? styles.dayHovered : ''}
                            `}
                            onMouseEnter={() => setHoverDate(d.date)}
                            onMouseLeave={() => setHoverDate(null)}
                            onClick={() => onSelect(d.date)}
                        >
                            {d.day}
                            {selected && <div className={styles.selectedDot} />}
                        </div>
                    );
                })}
            </div>
            <div className={styles.calendarFooter}>
                <span className={styles.footerLabel}>Período</span>
                <span className={styles.footerVal}>{formatRange(activeStart, activeEnd)}</span>
            </div>
        </div>
    );
};

interface CustomFilterContentProps {
    dataInicio: string;
    dataFim: string;
    onApply: (vals: { dataInicio: string; dataFim: string }) => void;
}

const CustomFilterContent: React.FC<CustomFilterContentProps> = ({ dataInicio, dataFim, onApply }) => {
    const [tempInicio, setTempInicio] = useState(dataInicio);
    const [tempFim, setTempFim] = useState(dataFim);
    const [isApplying, setIsApplying] = useState(false);
    const endInputRef = useRef<HTMLInputElement>(null);

    const handleApply = () => {
        setIsApplying(true);
        setTimeout(() => {
            onApply({ dataInicio: tempInicio, dataFim: tempFim });
            setIsApplying(false);
        }, 300);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, nextField: string) => {
        if (e.key === 'Enter') {
            if (nextField === 'end') {
                endInputRef.current?.focus();
            } else {
                handleApply();
            }
        }
    };

    return (
        <div className={styles.customContainer}>
            <h4 className={styles.customTitle}>Período Customizado</h4>
            <div className={styles.inputsStack}>
                <div className={styles.inputGroup}>
                    <label className={styles.inputLabel}>Início</label>
                    <input
                        type="date"
                        value={tempInicio}
                        onChange={(e) => setTempInicio(e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, 'end')}
                        className={styles.dateInput}
                    />
                </div>
                <div className={styles.inputGroup}>
                    <label className={styles.inputLabel}>Fim</label>
                    <input
                        ref={endInputRef}
                        type="date"
                        value={tempFim}
                        onChange={(e) => setTempFim(e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, 'apply')}
                        className={styles.dateInput}
                    />
                </div>
                <button
                    onClick={handleApply}
                    disabled={isApplying}
                    className={`${styles.applyBtn} ${isApplying ? styles.applyBtnActive : ''}`}
                >
                    {isApplying ? 'Aplicado!' : 'Aplicar Filtro'}
                </button>
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════ */
export interface DateNavigatorProps {
    periodo: '12meses' | 'anual' | 'mensal' | 'semanal' | 'custom';
    dataRef: string;
    dataInicio: string;
    dataFim: string;
    onChange: (vals: {
        periodo?: any;
        dataRef?: string;
        dataInicio?: string;
        dataFim?: string;
    }) => void;
    label?: string;
    isFiscal?: boolean;
    displayRange?: string;
}

export const DateNavigator: React.FC<DateNavigatorProps> = ({ 
    periodo, 
    dataRef, 
    dataInicio, 
    dataFim, 
    onChange, 
    label = "Período",
    isFiscal = false,
    displayRange,
}) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const h = (e: MouseEvent) => { 
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false); 
            }
        };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);

    // Memoize stable date objects to avoid unnecessary effect triggers in sub-components
    const activeDateObj = React.useMemo(() => {
        const d = new Date(dataRef + (dataRef.length <= 10 ? 'T12:00:00' : ''));
        return isNaN(d.getTime()) ? new Date() : d;
    }, [dataRef]);

    const activeRange = periodo === 'custom'
        ? (dataInicio && dataFim ? formatRange(new Date(dataInicio + 'T12:00:00'), new Date(dataFim + 'T12:00:00')) : "Selecionar datas")
        : periodo === 'semanal'
            ? formatRange(getWeekRange(activeDateObj).start, getWeekRange(activeDateObj).end)
            : periodo === 'anual'
                ? formatRange(getYearRange(activeDateObj).start, getYearRange(activeDateObj).end)
                : (periodo === '12meses' || periodo === 'mensal')
                    ? `${MONTHS[activeDateObj.getMonth()]} / ${activeDateObj.getFullYear()}`
                    : isFiscal 
                        ? (() => {
                            const m = activeDateObj.getMonth();
                            const y = activeDateObj.getFullYear();
                            return formatRange(new Date(y, m - 1, 26, 0, 0, 0, 0), new Date(y, m, 25, 23, 59, 59, 999));
                          })()
                        : formatRange(getMonthRange(activeDateObj).start, getMonthRange(activeDateObj).end);

    return (
        <div className={styles.navigatorContainer} ref={ref}>
            {label && <label className={styles.navigatorLabel}>{label}</label>}
            <button
                onClick={() => setOpen(!open)}
                className={styles.navigatorButton}
            >
                <div className={styles.iconWrapper}>
                    <CalendarTodayIcon style={{ fontSize: 14 }} />
                </div>
                <div className={styles.textWrapper}>
                    <span className={styles.periodText}>
                        {periodo === '12meses' ? 'Últimos 12 Meses' :
                         periodo === 'custom' ? 'Personalizado' : 
                         periodo === 'mensal' ? 'Mensal' : 
                         periodo === 'semanal' ? 'Semanal' : 
                         periodo === 'anual' ? 'Anual' : periodo}
                    </span>
                    <span className={styles.rangeText}>{displayRange || activeRange}</span>
                </div>
                <div className={`${styles.chevronIcon} ${open ? styles.chevronOpen : ''}`}>
                    <ChevronRightIcon style={{ fontSize: 16 }} />
                </div>
            </button>

            {open && (
                <div className={styles.dropdown}>
                    {periodo === 'semanal' && (
                        <MiniCalendar
                            activeDate={activeDateObj}
                            onSelect={(date) => {
                                onChange({ dataRef: formatLocalDate(date) });
                                setOpen(false);
                            }}
                        />
                    )}
                    {periodo === 'anual' && (
                        <div className={styles.yearContainer}>
                            <h4 className={styles.yearTitle}>Selecionar Ano</h4>
                            <div className={styles.yearList}>
                                {[2024, 2025, 2026, 2027].map(y => {
                                    const isSel = activeDateObj.getFullYear() === y;
                                    return (
                                        <button
                                            key={y}
                                            onClick={() => {
                                                const d = new Date(activeDateObj);
                                                d.setFullYear(y);
                                                onChange({ dataRef: formatLocalDate(d) });
                                                setOpen(false);
                                            }}
                                            className={`${styles.yearBtn} ${isSel ? styles.yearBtnSelected : ''}`}
                                        >
                                            {y}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {(periodo === 'mensal' || periodo === '12meses') && (
                        <div className={styles.monthContainer}>
                            <h4 className={styles.monthTitle}>Selecionar Mês</h4>
                            <div className={styles.monthsGrid}>
                                {MONTHS.map((m, i) => {
                                    const isSel = activeDateObj.getMonth() === i;
                                    return (
                                        <button
                                            key={m}
                                            onClick={() => {
                                                const d = new Date(activeDateObj);
                                                d.setMonth(i);
                                                onChange({ dataRef: formatLocalDate(d) });
                                                setOpen(false);
                                            }}
                                            className={`${styles.monthBtn} ${isSel ? styles.monthBtnSelected : ''}`}
                                        >
                                            {m.slice(0, 3)}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className={styles.yearSelectorFooter}>
                                {[2024, 2025, 2026, 2027].map(y => {
                                    const isSel = activeDateObj.getFullYear() === y;
                                    return (
                                        <button
                                            key={y}
                                            onClick={() => {
                                                const d = new Date(activeDateObj);
                                                d.setFullYear(y);
                                                onChange({ dataRef: formatLocalDate(d) });
                                            }}
                                            className={`${styles.yearFooterBtn} ${isSel ? styles.yearFooterBtnSelected : ''}`}
                                        >
                                            {y}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {periodo === 'custom' && (
                        <CustomFilterContent
                            dataInicio={dataInicio}
                            dataFim={dataFim}
                            onApply={(vals) => {
                                onChange(vals);
                                setOpen(false);
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    );
};
